import { Navigate, Route, Routes } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from './auth/AuthContext.js';
import { AppShell } from './app/AppShell.js';
import { LoginPage } from './pages/LoginPage.js';
import { VerifyPage } from './pages/VerifyPage.js';
import { OnboardingPage } from './pages/OnboardingPage.js';
import { PricingPage } from './pages/PricingPage.js';
import { TimelinePage } from './pages/TimelinePage.js';
import { SearchPage } from './pages/SearchPage.js';
import { DuplicatesPage } from './pages/DuplicatesPage.js';
import { CollectionsPage } from './pages/CollectionsPage.js';
import { CollectionDetailPage } from './pages/CollectionDetailPage.js';
import { ImportPage } from './pages/ImportPage.js';
import { SettingsPage } from './pages/SettingsPage.js';
import { TrashPage } from './pages/TrashPage.js';
import { BillingPage } from './pages/BillingPage.js';
import { SharePublicPage } from './pages/SharePublicPage.js';

function RequireAuth({ children }: { children: ReactNode }) {
  const { user, ready } = useAuth();
  if (!ready) return <FullScreenLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function FullScreenLoader() {
  return (
    <div style={{ height: '100vh', display: 'grid', placeItems: 'center', background: 'var(--bg)' }}>
      <span className="tv-skeleton" style={{ width: 120, height: 12, borderRadius: 6 }} />
    </div>
  );
}

export function App() {
  return (
    <Routes>
      {/* public */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/verify" element={<VerifyPage />} />
      <Route path="/s/:token" element={<SharePublicPage />} />
      <Route path="/onboarding" element={<OnboardingPage />} />
      <Route path="/pricing" element={<PricingPage />} />

      {/* app (auth-gated, inside the shell) */}
      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route index element={<Navigate to="/timeline" replace />} />
        <Route path="/timeline" element={<TimelinePage />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/duplicates" element={<DuplicatesPage />} />
        <Route path="/collections" element={<CollectionsPage />} />
        <Route path="/collections/:id" element={<CollectionDetailPage />} />
        <Route path="/import" element={<ImportPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/settings/trash" element={<TrashPage />} />
        <Route path="/billing" element={<BillingPage />} />
      </Route>

      <Route path="*" element={<Navigate to="/timeline" replace />} />
    </Routes>
  );
}
